import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { addMonths, format } from "date-fns";
import { CaretLeft, CaretRight, Warning } from "@phosphor-icons/react";
import api from "../lib/api";
import { inr0, monthStr, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { TxRow } from "../components/TxRow";

const Kpi = ({ label, value, className = "", testId, span = 1 }) => (
  <div data-testid={testId} className={`bg-white border border-border rounded-2xl p-4 ${span === 2 ? "col-span-2" : ""}`}>
    <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</p>
    <p className={`mt-1 text-2xl font-light tracking-tight tnum ${className}`}>{value}</p>
  </div>
);

export default function Dashboard() {
  const { household, member } = useAuth();
  const [cursor, setCursor] = useState(new Date());
  const [data, setData] = useState(null);
  const [lookups, setLookups] = useState(null);
  const month = monthStr(cursor);

  useEffect(() => {
    api.get("/lookups").then((r) => setLookups(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setData(null);
    api.get(`/dashboard?month=${month}`).then((r) => setData(r.data)).catch(() => {});
  }, [month]);

  const catNames = useMemo(() => Object.fromEntries((lookups?.categories || []).map((c) => [c.id, c.name])), [lookups]);
  const memberNames = useMemo(() => Object.fromEntries((lookups?.members || []).map((m) => [m.id, m.display_name])), [lookups]);
  const bizNames = useMemo(() => Object.fromEntries((lookups?.businesses || []).map((b) => [b.id, b.name])), [lookups]);

  const catRows = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.by_category)
      .map(([id, amt]) => ({ name: catNames[id] || "Uncategorized", amount: amt }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8);
  }, [data, catNames]);
  const maxCat = catRows[0]?.amount || 1;

  return (
    <div className="px-5 pt-8" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{household?.name}</p>
          <h1 className="text-2xl font-heading font-bold tracking-tight">Hello, {member?.display_name?.split(" ")[0]}</h1>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between bg-white border border-border rounded-2xl px-2 py-1">
        <button data-testid="month-prev" onClick={() => setCursor(addMonths(cursor, -1))} className="h-11 w-11 flex items-center justify-center rounded-xl active:bg-secondary">
          <CaretLeft size={18} />
        </button>
        <span data-testid="month-label" className="font-heading font-semibold">{format(cursor, "MMMM yyyy")}</span>
        <button data-testid="month-next" onClick={() => setCursor(addMonths(cursor, 1))} className="h-11 w-11 flex items-center justify-center rounded-xl active:bg-secondary">
          <CaretRight size={18} />
        </button>
      </div>

      {!data ? (
        <div className="mt-10 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
      ) : (
        <>
          {(data.alerts.draft_count > 0 || data.alerts.card_dues.length > 0) && (
            <div className="mt-4 space-y-2">
              {data.alerts.draft_count > 0 && (
                <Link to="/transactions?status=draft" data-testid="alert-drafts" className="flex items-center gap-2 bg-transferbg border border-border rounded-xl px-4 py-3 text-sm text-transfer">
                  <Warning size={16} /> {data.alerts.draft_count} draft transaction{data.alerts.draft_count > 1 ? "s" : ""} awaiting review
                </Link>
              )}
              {data.alerts.card_dues.map((d) => (
                <div key={d.account_id} data-testid="alert-card-due" className="flex items-center gap-2 bg-expensebg border border-border rounded-xl px-4 py-3 text-sm text-expense">
                  <Warning size={16} /> {d.name}: {inr0(d.outstanding)} due {fmtDate(d.due_date)}
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Kpi testId="kpi-income" label="Income received" value={inr0(data.income_total)} className="text-income" />
            <Kpi testId="kpi-expense" label="Expenses incurred" value={inr0(data.expense_incurred)} className="text-expense" />
            <Kpi testId="kpi-outflow" label="Cash / bank outflow" value={inr0(data.cash_outflow)} />
            <Kpi testId="kpi-repayments" label="Card repayments" value={inr0(data.card_repayments)} className="text-transfer" />
            <Kpi testId="kpi-card-outstanding" label="Credit-card outstanding (today)" value={inr0(data.card_outstanding)} className="text-expense" span={2} />
          </div>

          {data.daily_pace && data.daily_pace.days_elapsed > 0 && (
            <div className="mt-4 bg-white border border-border rounded-2xl p-4" data-testid="daily-pace-card">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Daily spending pace</p>
                <span data-testid="daily-pace-status" className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${data.daily_pace.variance >= 0 ? "bg-incomebg text-income" : "bg-expensebg text-expense"}`}>
                  {data.daily_pace.variance >= 0 ? "Within pace" : "Over pace"}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Income / day</p>
                  <p data-testid="pace-benchmark" className="text-sm font-semibold tnum">{inr0(data.daily_pace.benchmark)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Spend / day</p>
                  <p data-testid="pace-spend" className="text-sm font-semibold tnum">{inr0(data.daily_pace.avg_daily_spend)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Variance</p>
                  <p data-testid="pace-variance" className={`text-sm font-semibold tnum ${data.daily_pace.variance >= 0 ? "text-income" : "text-expense"}`}>
                    {data.daily_pace.variance >= 0 ? "+" : ""}{inr0(data.daily_pace.variance)}
                  </p>
                </div>
              </div>
              {data.daily_pace.series.length > 0 && (() => {
                const series = data.daily_pace.series.slice(-14);
                const peak = Math.max(data.daily_pace.benchmark, ...series.map((s) => s.spend), 1);
                return (
                  <div className="mt-3 relative h-16">
                    <div className="absolute left-0 right-0 border-t border-dashed border-transfer" style={{ bottom: `${(data.daily_pace.benchmark / peak) * 100}%` }} />
                    <div className="absolute inset-0 flex items-end gap-[3px]">
                      {series.map((s) => (
                        <div key={s.date} title={`${s.date}: ${inr0(s.spend)}`} className={`flex-1 rounded-t-sm ${s.spend > data.daily_pace.benchmark ? "bg-expense/80" : "bg-income/60"}`} style={{ height: `${Math.max(2, (Math.max(s.spend, 0) / peak) * 100)}%` }} />
                      ))}
                    </div>
                  </div>
                );
              })()}
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                Benchmark = this month's income received ÷ 30. Spend counts card purchases on the day incurred (not at repayment) — a pace view, not a cash-balance forecast.
              </p>
            </div>
          )}

          {Object.keys(data.income_by_member).length > 0 && (
            <div className="mt-4 bg-white border border-border rounded-2xl p-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Income by member</p>
              <div className="mt-2 space-y-1">
                {Object.entries(data.income_by_member).map(([id, amt]) => (
                  <div key={id} className="flex justify-between text-sm">
                    <span>{memberNames[id] || "Unassigned"}</span>
                    <span className="font-medium tnum text-income">{inr0(amt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {catRows.length > 0 && (
            <div className="mt-4 bg-white border border-border rounded-2xl p-4" data-testid="category-chart">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Spending by category</p>
              <div className="mt-3 space-y-2.5">
                {catRows.map((r) => (
                  <div key={r.name}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium">{r.name}</span>
                      <span className="tnum text-muted-foreground">{inr0(r.amount)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-expense/80" style={{ width: `${Math.max(4, (r.amount / maxCat) * 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.keys(data.by_business).length > 0 && (
            <div className="mt-4 bg-white border border-border rounded-2xl p-4">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Business spend</p>
              <div className="mt-2 space-y-1">
                {Object.entries(data.by_business).map(([id, amt]) => (
                  <div key={id} className="flex justify-between text-sm">
                    <span>{bizNames[id] || "Business"}</span>
                    <span className="font-medium tnum">{inr0(amt)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-heading font-semibold">Recent activity</h2>
              <Link to="/transactions" data-testid="link-all-transactions" className="text-sm text-muted-foreground underline underline-offset-4">See all</Link>
            </div>
            <div className="mt-2 bg-white border border-border rounded-2xl px-4 py-1">
              {data.recent.length === 0 ? (
                <p className="py-6 text-sm text-muted-foreground text-center">No transactions yet. Tap + to add your first one.</p>
              ) : (
                data.recent.map((tx) => <TxRow key={tx.id} tx={tx} categories={catNames} />)
              )}
            </div>
          </div>
        </>
      )}
      <div className="h-6" />
    </div>
  );
}
